import { SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMutation } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import {
  ConfigProvider,
  Drawer,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  TableColumnsType,
  Typography,
} from 'antd';
import { QUERY_ADD_CUSTOM_SALEPLAN } from 'apis/GraphQL/Queries/salePlan';
import {
  IUpdateManySalePlanningCustomerRevenue,
  VARIABLE_ADD_CUSTOM_SALEPLAN,
} from 'apis/GraphQL/Variables/salePlan';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import SalePlanApi from 'apis/SalePlanApis';
import {
  EditIcon,
  FileGrayIcon,
  FileIcon,
  PlusIcon,
  XGrayIcon,
  XIcon,
} from 'assets/svg';
import { Button } from 'components';
import ButtonAction from 'components/Button/ButtonAction';
import { Rows } from 'components/Table/Row';
import TableMoveRow, { DragIndex } from 'components/Table/TableMoveRow';
import dayjs from 'dayjs';
import { IGetCustomerByBranchId } from 'interfaces/Customer';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import { IRouteInfo, IUpdateSalePlanningIndexItem } from 'interfaces/SaleLine';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import formatNumber, {
  formatCurrency,
  toastError,
  toastSuccess,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { openNotificationWithIcon } from 'utils/notification';
import ModalAddCustomerToRoute from './ModalAddCustomerToRoute/ModalAddCustomerToRoute';

interface Iprops {
  setOpen: any;
  open: boolean;
  children?: ReactNode;
  salePlaningId: number;
  routeInfo?: IRouteInfo;
  implementationDate?: string;
  statusId: string;
  refetch: (type?: any, headerRow?: any) => void;
}

const { SaveTwoTone, CloseCircleTwoTone } = icons;

export default function DrawerSalePlan({
  statusId,
  setOpen,
  open,
  salePlaningId,
  routeInfo,
  implementationDate,
  refetch,
}: Iprops) {
  const { DragHandle, onDragEnd } = Rows;
  const [edit] = useState(-1);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [data, setData] = useState<any>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchCustomer, setSearchCustomer] = useState('');
  const searchCustomerDebounce = useDebounce(searchCustomer, 500);
  const searchRef = useRef(false);
  const disabledAddBtn = statusId === 'A107_03' || statusId === 'A107_04';
  const [isEdit, setIsEdit] = useState(false);
  const [form] = Form.useForm();
  const [dataDetail, setDataDetail] = useState<any[]>([]);
  const [cusReason, setCusReason] = useState<{
    id: string;
    sapCustomerId: string;
    storeName: string;
  } | null>(null);
  const [reasons, setReasons] = useState<any[]>([]);

  const onClose = () => {
    setOpen(false);
  };

  const { isPending: isAllSalePlanPending, mutate: getAllSalePlan } =
    useMutation({
      mutationFn: (params: IGetCustomerByBranchId) =>
        SalePlanApi.getCustomersInSalePlan(params),
      onSuccess: (data: any) => {
        const items = data?.data?.getSalesPlanningCust?.items || [];

        setData(items);
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const { isPending: deleteUserLoading, mutate: deleteUser } = useMutation({
    mutationFn: SalePlanApi.deleteCustomerInSalePlan,
    onSuccess: (data: any) => {
      const id = data?.data?.updateSalesPlanningCust?.id;

      if (id == null || id == undefined) {
        openNotificationWithIcon(
          'error',
          'Xóa không thành công',
          data?.data?.deleteSalesPlanningCustIf?.message,
        );
      } else {
        getAllSalePlan({
          salesPlanningId: salePlaningId,
        });
        openNotificationWithIcon('success', 'Xoá thành công');
        refetch();
      }
    },
    onError: (error) => {
      openNotificationWithIcon('error', 'Xóa không thành công', error.message);
    },
  });

  const mutationAddCusToSalePlaning = useMutation({
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: () => {
      setOpenModal(false);
      toastSuccess('Thêm khách hàng thành công');
      refetch();
    },
    onError: (error) => {
      console.log(error);
      toastError('Thêm khách hàng thất bại');
    },
  });

  const updateSalePlanningIndex = useMutation({
    mutationFn: (params: IUpdateSalePlanningIndexItem[]) =>
      SalePlanApi.updateSalePlanningIndexItem(params),
    onSuccess: () => {
      toastSuccess('Cập nhật thứ tự thành công');
    },
    onError: () => {
      toastError('Cập nhật thứ tự thất bại');
    },
  });

  const { mutate: updateManyRevenue } = useMutation({
    mutationFn: (params: IUpdateManySalePlanningCustomerRevenue[]) =>
      SalePlanApi.updateManySalePlanningCustomerRevenue(params),
    onSuccess: (data) => {
      if (data.data?.updateManySalesPlanningCust?.length >= 0) {
        toastSuccess('Cập nhật doanh thu trung bình thành công');

        const trimSearch = searchCustomerDebounce.trim();

        getAllSalePlan({
          salesPlanningId: salePlaningId,
          customerId: trimSearch,
          customerName: trimSearch,
        });
        refetch();

        setIsEdit(false);
        form.resetFields();
      } else {
        toastError('Cập nhật doanh thu trung bình thất bại');
      }
    },
    onError: () => {
      toastError('Cập nhật doanh thu trung bình thất bại');
    },
  });

  const handleOnChange = (value: any, record: any, fieldName: string) => {
    const valueWithoutDots = value.replace(/\./g, '').replace(',', '.');
    const valueNumber = Number(valueWithoutDots);
    const valueWithDots = formatNumber(valueNumber);

    if (valueNumber < 0) {
      toast.dismiss();
      toastError('Doanh thu trung bình không được âm!');
    } else if (!/^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/.test(valueWithDots)) {
      toast.dismiss();
      toastError('Doanh thu trung bình chỉ được nhập số!');
    }

    if (isNaN(valueWithoutDots)) {
      form.setFieldValue(fieldName, value);
      return;
    }

    form.setFieldValue(fieldName, valueWithDots);
  };

  const handleExpectedRevenueFocus = useCallback(
    (record: any) => {
      if (!record || !record?.id) return;

      if (dataDetail.length <= 0) {
        setDataDetail([record]);
        return;
      }

      setDataDetail((prev: any[]) => {
        let isExist = false;
        const oldData = prev.map((item: any) => {
          if (item.id === record.id) {
            isExist = true;
            return item;
          }
          return item;
        });

        if (!isExist) oldData.push(record);

        return oldData;
      });
    },
    [dataDetail],
  );

  useEffect(() => {
    if (!open) {
      setSearchCustomer('');
      setPage(1);
      setPageSize(10);
      setIsEdit(false);
      setDataDetail([]);
      form.resetFields();
    }
  }, [form, open]);

  useEffect(() => {
    if (!open) return;

    if (searchRef.current) {
      searchRef.current = false;
      return;
    }

    const trimSearch = searchCustomerDebounce.trim();

    setPage(1);

    getAllSalePlan({
      salesPlanningId: salePlaningId,
      customerId: trimSearch,
      customerName: trimSearch,
    });
  }, [getAllSalePlan, open, salePlaningId, searchCustomerDebounce]);

  const handleClickReason = (record: any) => {
    setCusReason({
      id: record?.customer?.id,
      sapCustomerId: record?.customer?.sapCustomerId,
      storeName: record?.customer?.storeName,
    });
    setReasons(record?.tsalesPlanningCustRemoveReason || []);
  };

  const columns: TableColumnsType = [
    {
      // title: "Thao tác",
      className: 'shadow-right',
      width: 120, // 120
      fixed: 'left',
      align: 'center',
      render: (_: any, record: any, index: number) =>
        edit == index ? (
          <Space size={'middle'} className="text-lg">
            <ButtonAction tooltip="Lưu">
              <SaveTwoTone style={{ fontSize: 20 }} className="" />
            </ButtonAction>
            <ButtonAction tooltip="Hủy">
              <CloseCircleTwoTone style={{ fontSize: 20 }} />
            </ButtonAction>
          </Space>
        ) : (
          <Space size={'middle'} className="text-lg ">
            <DragHandle title={'Di chuyển'} />
            <ButtonAction
              onClick={() => handleClickReason(record)}
              disabled={record?.tsalesPlanningCustRemoveReason?.length < 1}
              tooltip="Lịch sử hủy"
            >
              {record?.tsalesPlanningCustRemoveReason?.length < 1 ? (
                <FileGrayIcon />
              ) : (
                <FileIcon />
              )}
            </ButtonAction>
            {/* check role */}
            <ButtonAction
              tooltip="Xóa"
              onClick={() => {
                deleteUser({
                  cusSalePlantId: record?.id,
                });
              }} /* check role */
              disabled={disabledAddBtn}
            >
              {edit == -1 ? <XIcon /> : <XGrayIcon />}
            </ButtonAction>
          </Space>
        ),
    },
    {
      title: 'STT',
      align: 'center',
      dataIndex: 'key',
      className: 'align-top',
      width: 50,
      render: (_: any, __: any, index: number) =>
        (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Mã khách hàng',
      dataIndex: 'customerId',
      className: 'align-top',
      width: 170,
      render: (_, record) =>
        record.customer?.sapCustomerId || record?.customerId || '-',
    },
    {
      title: 'Tên cửa hàng',
      className: 'align-top',
      width: 250,
      render: (record: any) => {
        return record?.customer?.storeName || '-';
      },
    },
    // {
    //   title: 'Vip công',
    //   className: 'align-top',
    //   width: 100,
    //   render: (record: any) => {
    //     return record?.customer?.trankProcess?.name || '-';
    //   },
    // },
    // {
    //   title: 'Vip món',
    //   className: 'align-top',
    //   width: 100,
    //   render: (record: any) => {
    //     return record?.customer?.trankProduct?.name || '-';
    //   },
    // },
    {
      title: 'Hạng thẻ',
      className: 'align-top',
      width: 100,
      render: (record: any) => {
        return record?.customer?.trank?.name || '-';
      },
    },
    {
      title: 'SDT',
      className: 'align-top',
      width: 140,
      render: (record: any) => {
        return record?.customer?.phone || '-';
      },
    },
    {
      title: 'MST',
      className: 'align-top',
      width: 140,
      render: (record: any) => {
        return record?.customer?.taxId || '-';
      },
    },
    {
      title: 'CCCD',
      className: 'align-top',
      width: 140,
      render: (record: any) => {
        return record?.customer?.cccd || '-';
      },
    },
    {
      title: 'Doanh thu trung bình',
      className: 'align-top',
      width: 200,
      align: 'right',
      dataIndex: 'averageRevenue',
      render: (value, record) => {
        const isValidValue = value !== null && value !== undefined;
        const valueFormatted = isValidValue ? formatNumber(value) : value;
        const fieldName = `expectedRevenue_${record?.id}`;

        return isEdit ? (
          <Form.Item
            name={fieldName}
            className="m-0"
            initialValue={valueFormatted}
            rules={[
              {
                pattern: /^(?:\d{1,3}(?:\.\d{3})*|\d+)(\.\d+)?$/,
                message: '',
              },
            ]}
          >
            <Input
              allowClear
              value={valueFormatted}
              className="w-full"
              onChange={(e) =>
                handleOnChange(e.target.value, record, fieldName)
              }
              onFocus={() => handleExpectedRevenueFocus(record)}
            />
          </Form.Item>
        ) : isValidValue ? (
          formatCurrency({
            value,
            round: true,
          })
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Địa chỉ chi tiết',
      className: 'align-top',
      width: 180,
      render: (record: any) => {
        return record?.customer?.address || '-';
      },
    },
    {
      title: 'Phường/Xã',
      className: 'align-top',
      width: 180,
      render: (record: any) => {
        return record?.customer?.tward?.name || '-';
      },
    },
    {
      title: 'Quận/Huyện',
      className: 'align-top',
      width: 180,
      render: (record: any) => {
        return record?.customer?.tdistrict?.name || '-';
      },
    },
    {
      title: 'Tỉnh/Thành Phố',
      className: 'align-top',
      width: 140,
      render: (record: any) => {
        return record?.customer?.tcity?.name || '-';
      },
    },
  ];

  const handleChange = (event: any) => {
    setSearchCustomer(event.target.value);
  };

  const handleOK = async (newCustomer: any) => {
    await mutationAddCusToSalePlaning.mutateAsync({
      query: QUERY_ADD_CUSTOM_SALEPLAN,
      variables: VARIABLE_ADD_CUSTOM_SALEPLAN({
        salesPlanningId: salePlaningId,
        listCustomer: newCustomer,
      }),
    });

    searchRef.current = true;
    setSearchCustomer('');
    getAllSalePlan({
      salesPlanningId: salePlaningId,
    });
  };

  const handleChangeRow = (dragRow: DragIndex) => {
    const { activeIndex, overIndex } = dragRow;

    if (
      activeIndex != undefined &&
      overIndex != undefined &&
      activeIndex != overIndex
    ) {
      const activeItem = data[activeIndex];
      const overItem = data[overIndex];

      updateSalePlanningIndex.mutate([
        {
          id: activeItem.id,
          indexNum: overItem.indexNum,
        },
        {
          id: overItem.id,
          indexNum: activeItem.indexNum,
        },
      ]);
    }
  };

  const handleClickEditBtn = async () => {
    try {
      if (!isEdit) {
        setIsEdit(true);
        form.resetFields();
        setDataDetail([]);
        return;
      }

      if (!(await form.validateFields())) return;

      if (dataDetail.length <= 0) {
        setIsEdit(false);
        return;
      }

      const formValues = form.getFieldsValue();
      const expectedRevenueValues = Object.keys(form.getFieldsValue()).reduce(
        (acc, key) => {
          const [, id] = key.split('_');
          const valueWithoutDots = formValues[key]
            ? formValues[key].replace(/\./g, '').replace(',', '.')
            : null;

          acc[id] =
            valueWithoutDots !== null && Number.isInteger(+valueWithoutDots)
              ? Number(valueWithoutDots)
              : null;

          return acc;
        },
        {} as any,
      );

      updateManyRevenue(
        Object.keys(expectedRevenueValues)
          .map((id) => ({
            id,
            averageRevenue: expectedRevenueValues[id],
          }))
          .filter((item) => Number.isInteger(item.averageRevenue)),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleLClickAddBtn = async () => {
    try {
      if (isEdit) {
        setIsEdit(false);
        form.resetFields();
        return;
      }

      setOpenModal(true);
    } catch (error) {
      //
    }
  };

  return (
    <Drawer
      forceRender
      width={715}
      title={
        <Typography.Text className="text-[#003056] font-bold text-2xl">
          Khách hàng trong kế hoạch bán hàng
        </Typography.Text>
      }
      onClose={onClose}
      open={open}
      closable={false}
      destroyOnClose
      extra={<ButtonAction title={<XGrayIcon />} onClick={onClose} />}
    >
      <Space className="flex mb-3 justify-end mr-5">
        <ConfigProvider
          theme={{
            token: {
              borderRadius: 50,
            },
          }}
        >
          <Input
            allowClear
            value={searchCustomer}
            placeholder="Tìm khách hàng theo tên, mã"
            className="p-2 w-64 bg-[#EFF3F8]"
            prefix={<SearchOutlined className="mx-1" />}
            onChange={handleChange}
          />
        </ConfigProvider>

        <Button
          customClass="bg-white"
          disabled={disabledAddBtn || !data?.length}
          title={
            isEdit ? <SaveOutlined style={{ fontSize: 18 }} /> : <EditIcon />
          }
          handleOnclick={handleClickEditBtn}
        />
        <Button
          customClass="bg-white"
          title={isEdit ? <XIcon /> : <PlusIcon />}
          handleOnclick={handleLClickAddBtn}
          disabled={disabledAddBtn}
        />
      </Space>

      <ModalAddCustomerToRoute
        routeInfo={routeInfo}
        open={openModal}
        setOpen={setOpenModal}
        searchId={salePlaningId}
        handleOK={handleOK}
        implementationDate={implementationDate}
      />

      <Form form={form}>
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={(e) => onDragEnd(e, setData)}
        >
          <SortableContext
            items={(data || [])?.map((i: any) => i.key)}
            strategy={verticalListSortingStrategy}
          >
            <TableMoveRow
              rowKey="id"
              setDataSource={setData}
              dataSource={data || []}
              columns={columns}
              loading={
                updateSalePlanningIndex.isPending ||
                isAllSalePlanPending ||
                deleteUserLoading ||
                mutationAddCusToSalePlaning.isPending
              }
              scroll={{ x: 875 }}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: data?.length || 0,
                onChange: (page, pageSize) => {
                  setPage(page);
                  setPageSize(pageSize);
                },
              }}
              onChangeRow={handleChangeRow}
            />
          </SortableContext>
        </DndContext>
      </Form>
      <Modal
        centered
        title="Lịch sử hủy"
        open={!!cusReason}
        onCancel={() => setCusReason(null)}
        okButtonProps={{
          hidden: true,
        }}
        cancelText="Đóng lại"
      >
        <Flex vertical gap={12}>
          <Typography.Title
            level={5}
            className="text-[#003056]"
            style={{
              marginBottom: 0,
            }}
          >
            {cusReason?.sapCustomerId || cusReason?.id}
            &nbsp;-&nbsp;
            {cusReason?.storeName}
          </Typography.Title>
          <div className="max-h-[70vh] overflow-y-auto -mr-6">
            {reasons.map((reason) => (
              <Flex key={reason?.id} vertical>
                <Typography.Text>
                  {dayjs(reason?.createDate).format('HH:mm DD/MM/YYYY')}
                  &nbsp;
                  {reason.tcreateUser.fullName}
                </Typography.Text>
                <Typography.Text>{reason?.reason}</Typography.Text>
              </Flex>
            ))}
          </div>
        </Flex>
      </Modal>
    </Drawer>
  );
}
